/**
 * Utilidades de color sin dependencias: parseo hex, contraste WCAG 2.x y OKLCH.
 * Se usan para derivar escalas, auditar contraste y tematizar por tenant en tiempo de ejecución.
 */
const HEX_PATTERN = /^#([0-9a-f]{6})$/i;
export function isHex(value) {
    return HEX_PATTERN.test(value);
}
export function hexToRgb(hex) {
    const match = HEX_PATTERN.exec(hex);
    if (!match?.[1])
        throw new Error(`Color hex inválido: ${hex}`);
    const n = Number.parseInt(match[1], 16);
    return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}
export function rgbToHex({ r, g, b }) {
    const part = (v) => Math.round(Math.min(255, Math.max(0, v)))
        .toString(16)
        .padStart(2, '0');
    return `#${part(r)}${part(g)}${part(b)}`.toUpperCase();
}
function srgbToLinear(channel) {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
function linearToSrgb(value) {
    const v = value <= 0.0031308 ? 12.92 * value : 1.055 * value ** (1 / 2.4) - 0.055;
    return v * 255;
}
/** Luminancia relativa WCAG 2.x (0 = negro, 1 = blanco). */
export function relativeLuminance(hex) {
    const { r, g, b } = hexToRgb(hex);
    return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}
/** Razón de contraste WCAG 2.x entre dos colores (1–21). */
export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const [hi, lo] = la > lb ? [la, lb] : [lb, la];
    return (hi + 0.05) / (lo + 0.05);
}
/** Umbrales WCAG 2.1 nivel AA. */
export const AA = {
    /** Texto normal (< 18.66 px negrita o < 24 px). */
    text: 4.5,
    /** Texto grande (≥ 18.66 px negrita o ≥ 24 px). */
    largeText: 3,
    /** Componentes de interfaz y gráficos (bordes de campos, foco, íconos). */
    ui: 3,
};
/** El color de texto (de los candidatos) con mayor contraste sobre `background`. */
export function bestForeground(background, candidates) {
    let best;
    let bestRatio = 0;
    for (const candidate of candidates) {
        const ratio = contrastRatio(candidate, background);
        if (ratio > bestRatio) {
            best = candidate;
            bestRatio = ratio;
        }
    }
    if (!best)
        throw new Error('bestForeground requiere al menos un candidato');
    return best;
}
// --- OKLab / OKLCH (Björn Ottosson) -------------------------------------------------------
export function hexToOklch(hex) {
    const { r, g, b } = hexToRgb(hex);
    const lr = srgbToLinear(r);
    const lg = srgbToLinear(g);
    const lb = srgbToLinear(b);
    const l_ = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
    const m_ = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
    const s_ = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
    const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
    const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
    const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
    const c = Math.sqrt(A * A + B * B);
    let h = (Math.atan2(B, A) * 180) / Math.PI;
    if (h < 0)
        h += 360;
    return { l: L, c, h };
}
/** Convierte OKLCH a sRGB lineal sin recortar (puede salir de [0, 1]). */
function oklchToLinearRgb({ l, c, h }) {
    const rad = (h * Math.PI) / 180;
    const A = c * Math.cos(rad);
    const B = c * Math.sin(rad);
    const l_ = l + 0.3963377774 * A + 0.2158037573 * B;
    const m_ = l - 0.1055613458 * A - 0.0638541728 * B;
    const s_ = l - 0.0894841775 * A - 1.291485548 * B;
    const L = l_ ** 3;
    const M = m_ ** 3;
    const S = s_ ** 3;
    return [
        4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S,
        -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S,
        -0.0041960863 * L - 0.7034186147 * M + 1.707614701 * S,
    ];
}
const GAMUT_EPSILON = 1e-6;
function inGamut(rgb) {
    return rgb.every((v) => v >= -GAMUT_EPSILON && v <= 1 + GAMUT_EPSILON);
}
/** OKLCH → hex, reduciendo croma (búsqueda binaria) hasta entrar en la gama sRGB. */
export function oklchToHex(color) {
    let rgb = oklchToLinearRgb(color);
    if (!inGamut(rgb)) {
        let lo = 0;
        let hi = color.c;
        for (let i = 0; i < 24; i++) {
            const mid = (lo + hi) / 2;
            if (inGamut(oklchToLinearRgb({ ...color, c: mid })))
                lo = mid;
            else
                hi = mid;
        }
        rgb = oklchToLinearRgb({ ...color, c: lo });
    }
    const [r, g, b] = rgb.map((v) => linearToSrgb(Math.min(1, Math.max(0, v))));
    return rgbToHex({ r, g, b });
}
//# sourceMappingURL=color.js.map